// Pure state machine for a single incoming-call ring lifecycle.
//
// iOS-enforced contract (reproduced as policy constants on web):
//   - osDeadlineSeconds (18): a VoIP push must be reported to CallKit within
//     ~20s on iOS. On web there is no OS enforcement, but we mirror the same
//     18s internal deadline so the logic is symmetric: a Service Worker that
//     honours this deadline can synthesise an immediate "ended" notification
//     and satisfy the Notification API contract before the user ever sees it.
//   - answerDeadlineSeconds (30): after we reach .ringing the user has 30s to
//     accept before we mark the call .missed. Matches Signal's default.
//
// The transition function is pure so the self-test can advance time with an
// injected `now` without touching the real clock. Invalid transitions return
// null — the caller logs but does not crash.
//
// iOS counterpart: kaether_for_ios/Stoat/E2EE/CallRingingState.swift

import type { CallKind } from "./CallRingingPayload";

// ---------------------------------------------------------------------------
// State + Event enums
// ---------------------------------------------------------------------------

/**
 * Ring lifecycle states. Names and semantics are identical to the iOS enum
 * so state serialised from one platform can be read on the other.
 */
export type CallRingState =
  | "incoming"   // push arrived; descriptor not yet fetched
  | "ringing"    // descriptor decrypted; notification is visible to user
  | "answered"   // user accepted; media tracks being negotiated
  | "declined"   // user explicitly declined
  | "missed"     // ringing deadline elapsed without answer
  | "timedOut"   // incoming deadline elapsed without reaching ringing
  | "ended";     // call completed normally or remote hung up

/**
 * Events that drive state transitions.
 */
export type CallRingEvent =
  | { type: "pushArrived"; callId: string; at: number }
  | { type: "descriptorFetched"; callId: string }
  | { type: "userAnswered" }
  | { type: "userDeclined" }
  | { type: "remoteHungUp" }
  | { type: "osTimeout" }
  | { type: "appShutdown" };

// ---------------------------------------------------------------------------
// Session value type
// ---------------------------------------------------------------------------

export interface CallRingingSession {
  readonly callId: string;
  readonly callerUserId: string;
  readonly kind: CallKind;
  readonly pushedAt: number;   // unix seconds
  readonly state: CallRingState;
  /**
   * Wall-clock time (unix seconds) of the most recent state-mutating event.
   * Used by `tick(...)` to enforce the answer-deadline countdown.
   */
  readonly lastEventAt: number;
}

// ---------------------------------------------------------------------------
// Transition table (pure)
// ---------------------------------------------------------------------------

export const CallRingingTransitions = {
  /**
   * iOS-enforced contract: PushKit gives ~20s after VoIP-push arrival to
   * report to CallKit. We budget 18s and synthesise `.timedOut` internally
   * before the OS enforces the kill, so we can still report "call ended" and
   * stay alive. On web there is no OS enforcement; we mirror the same
   * constant as policy so the state machine is symmetric across platforms.
   */
  osDeadlineSeconds: 18 as const,

  /**
   * After we reach `.ringing` the user has 30 seconds to answer before we
   * mark the call `.missed`. Matches Signal's default ring-time.
   */
  answerDeadlineSeconds: 30 as const,

  /**
   * Pure transition function. Returns the new session for valid transitions,
   * null for invalid ones (caller logs). `lastEventAt` is always refreshed
   * to `now` on a successful transition.
   */
  transition(
    session: CallRingingSession,
    event: CallRingEvent,
    now: number,
  ): CallRingingSession | null {
    const { state } = session;

    if (event.type === "pushArrived" && event.callId === session.callId && state === "incoming") {
      // Idempotent re-ring of the same call. State doesn't change but
      // lastEventAt updates so deadline tracking is correct.
      return advance(session, "incoming", now);
    }

    if (event.type === "descriptorFetched" && event.callId === session.callId && state === "incoming") {
      return advance(session, "ringing", now);
    }

    if (event.type === "osTimeout" && state === "incoming") {
      return advance(session, "timedOut", now);
    }

    if (event.type === "remoteHungUp" && state === "incoming") {
      // Caller cancelled before we got the descriptor.
      return advance(session, "timedOut", now);
    }

    if (event.type === "userAnswered" && state === "ringing") {
      return advance(session, "answered", now);
    }

    if (event.type === "userDeclined" && state === "ringing") {
      return advance(session, "declined", now);
    }

    if (event.type === "remoteHungUp" && state === "ringing") {
      // Caller hung up before we could pick up — it's a missed call.
      return advance(session, "missed", now);
    }

    if (
      (event.type === "remoteHungUp" || event.type === "userDeclined") &&
      state === "answered"
    ) {
      return advance(session, "ended", now);
    }

    if (event.type === "appShutdown") {
      return advance(session, "ended", now);
    }

    // Invalid transition.
    return null;
  },

  /**
   * Deadline-driven transition. Caller invokes this on a periodic timer (or
   * after every other event) to enforce the OS deadline and the user-answer
   * deadline without needing wall-clock notifications.
   *
   * Returns the updated session if a deadline fired, null otherwise.
   */
  tick(session: CallRingingSession, now: number): CallRingingSession | null {
    if (session.state === "incoming") {
      if (now - session.pushedAt > CallRingingTransitions.osDeadlineSeconds) {
        return advance(session, "timedOut", now);
      }
      return null;
    }
    if (session.state === "ringing") {
      if (now - session.lastEventAt > CallRingingTransitions.answerDeadlineSeconds) {
        return advance(session, "missed", now);
      }
      return null;
    }
    return null;
  },
} as const;

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function advance(
  session: CallRingingSession,
  newState: CallRingState,
  now: number,
): CallRingingSession {
  return { ...session, state: newState, lastEventAt: now };
}
